import React, { useState } from 'react';

import { Button, Card, Col, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import {
  faArrowLeft,
  faBoxOpen,
  faChartLine,
  faFileDownload,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { bootstrapSecondary, grape } from 'lib/css/colors';
import Container from 'components/shared/Container';
import DeleteEntityImages from 'components/internaltools/DeleteEntityImages';
import ManufacturerScoreGapReport from 'components/internaltools/ManufacturerScoreGapReport';
import ReportsDownload from 'components/internaltools/ReportsDownload';
import DeleteEntityProducts from './DeleteEntityProducts';

// Constants
const FUNCTION_IDS = {
  DELETE_IMAGES: 'delete-images',
  REPORTS_DOWNLOAD: 'reports-download',
  DELETE_PRODUCTS: 'delete-products',
  MFR_SCORE_GAP_REPORT: 'mfr-score-gap-report',
};

// Available functions grouped by category
const FUNCTION_CATEGORIES = [
  {
    id: 'data-management',
    title: 'Data Management',
    functions: [
      {
        id: FUNCTION_IDS.DELETE_IMAGES,
        title: 'Delete Entity Images',
        description: 'Delete all images associated with a vendor or manufacturer',
        icon: faImage,
      },
      {
        id: FUNCTION_IDS.DELETE_PRODUCTS,
        title: 'Delete Entity Products',
        description:
          'Delete products from vendors or manufacturers,' + ' with CSV filtering and UPB options',
        icon: faBoxOpen,
      },
    ],
  },
  {
    id: 'reports-analytics',
    title: 'Reports & Analytics',
    functions: [
      {
        id: FUNCTION_IDS.REPORTS_DOWNLOAD,
        title: 'Reports Download',
        description: 'Generate and download various reports such as engaged manufacturers with GMV',
        icon: faFileDownload,
      },
      {
        id: FUNCTION_IDS.MFR_SCORE_GAP_REPORT,
        title: 'Manufacturer Score Gap Report',
        description:
          'For a chosen manufacturer and FSA score threshold, list the missing attributes' +
          ' needed per section to reach the B-score target.',
        icon: faChartLine,
      },
    ],
  },
  // Add more categories here as needed in the future
];

function InternalToolsView() {
  const [selectedFunction, setSelectedFunction] = useState(null);

  const handleBackToMenu = () => {
    setSelectedFunction(null);
  };

  const handleFunctionSelect = functionId => {
    setSelectedFunction(functionId);
  };

  // Render main menu with available functions grouped by category
  const renderMainMenu = () => (
    <>
      <h1 className={css(styles.pageTitle)}>Internal Tools</h1>

      <Row>
        {FUNCTION_CATEGORIES.map(category => (
          <Col md={6} lg={4} key={category.id} className={css(styles.categoryColumn)}>
            <div className={css(styles.categorySection)}>
              <h2 className={css(styles.categoryTitle)}>{category.title}</h2>
              <div className={css(styles.functionsList)}>
                {category.functions.map(func => (
                  <div
                    key={func.id}
                    className={css(styles.functionItem)}
                    onClick={() => handleFunctionSelect(func.id)}
                  >
                    <FontAwesomeIcon icon={func.icon} className={css(styles.functionItemIcon)} />
                    <div className={css(styles.functionItemContent)}>
                      <div className={css(styles.functionItemTitle)}>{func.title}</div>
                      <div className={css(styles.functionItemDescription)}>{func.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );

  // Render delete images functionality
  const renderDeleteImages = () => (
    <>
      <Button
        variant="outline-primary"
        className={css(styles.backButton)}
        onClick={handleBackToMenu}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Menu
      </Button>

      <h1 className={css(styles.pageTitle)}>Delete Entity Images</h1>

      <DeleteEntityImages />
    </>
  );

  // Render reports download functionality
  const renderReportsDownload = () => (
    <>
      <Button
        variant="outline-primary"
        className={css(styles.backButton)}
        onClick={handleBackToMenu}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Menu
      </Button>

      <h1 className={css(styles.pageTitle)}>Reports Download</h1>

      <ReportsDownload />
    </>
  );

  // Render delete products functionality
  const renderDeleteProducts = () => (
    <>
      <Button
        variant="outline-primary"
        className={css(styles.backButton)}
        onClick={handleBackToMenu}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Menu
      </Button>

      <h1 className={css(styles.pageTitle)}>Delete Entity Products</h1>

      <DeleteEntityProducts />
    </>
  );

  // Render manufacturer score gap report functionality
  const renderMfrScoreGapReport = () => (
    <>
      <Button
        variant="outline-primary"
        className={css(styles.backButton)}
        onClick={handleBackToMenu}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Menu
      </Button>

      <h1 className={css(styles.pageTitle)}>Manufacturer Score Gap Report</h1>

      <ManufacturerScoreGapReport />
    </>
  );

  // Main render
  return (
    <Container className={css(styles.container)}>
      {!selectedFunction && renderMainMenu()}
      {selectedFunction === FUNCTION_IDS.DELETE_IMAGES && renderDeleteImages()}
      {selectedFunction === FUNCTION_IDS.REPORTS_DOWNLOAD && renderReportsDownload()}
      {selectedFunction === FUNCTION_IDS.DELETE_PRODUCTS && renderDeleteProducts()}
      {selectedFunction === FUNCTION_IDS.MFR_SCORE_GAP_REPORT && renderMfrScoreGapReport()}
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: '2rem',
    paddingBottom: '2rem',
  },
  pageTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '2rem',
  },
  categoryColumn: {
    marginBottom: '2rem',
  },
  categorySection: {
    height: '100%',
  },
  categoryTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: '#333',
    borderBottom: `2px solid ${grape}`,
    paddingBottom: '0.5rem',
  },
  functionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  functionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#f8f9fa',
      borderColor: grape,
      transform: 'translateX(4px)',
    },
  },
  functionItemIcon: {
    fontSize: '1.25rem',
    color: grape,
    marginTop: '0.25rem',
    flexShrink: 0,
  },
  functionItemContent: {
    flex: 1,
    minWidth: 0,
  },
  functionItemTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.25rem',
  },
  functionItemDescription: {
    fontSize: '0.85rem',
    color: bootstrapSecondary,
    lineHeight: '1.4',
  },
  backButton: {
    marginBottom: '1rem',
  },
});

export default InternalToolsView;
